import React, { useEffect, useState } from 'react';
import { NODE_CONFIGS, AgentNodeType } from './nodes/nodeTypes';
import { agentService, PromptTemplateDTO } from '../../services/agentService';
import { normaliseSections } from '../../utils/promptUtils';

interface NodePaletteProps {
    onDragStart: (event: React.DragEvent, nodeType: AgentNodeType, initialData?: any) => void;
}

const categories = [
    { key: 'input', title: 'Triggers', subtitle: 'How the agent starts' },
    { key: 'process', title: 'Actions', subtitle: 'What the agent does' },
    { key: 'tools', title: 'Tools', subtitle: 'External capabilities for AI' },
    { key: 'output', title: 'Outputs', subtitle: 'How the agent responds' },
];

const NodePalette: React.FC<NodePaletteProps> = ({ onDragStart }) => {
    const [templates, setTemplates] = useState<PromptTemplateDTO[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        agentService.getPromptTemplates()
            .then(data => setTemplates(data))
            .catch(err => console.error("Failed to load prompt templates", err))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="node-palette">
            <div className="node-palette__header">
                <h3 className="node-palette__title">Components</h3>
                <p className="node-palette__subtitle">Drag to add to canvas</p>
            </div>

            {/* Render Prompt Templates if available */}
            {templates.length > 0 && (
                <div className="node-palette__category">
                    <div className="node-palette__cat-header">
                        <span className="node-palette__cat-title">Recipes (Prompts)</span>
                        <span className="node-palette__cat-sub">Pre-configured AI Agents</span>
                    </div>
                    <div className="node-palette__items">
                        {templates.map((tpl) => (
                            <div
                                key={tpl.templateId}
                                className="node-palette__item"
                                draggable
                                onDragStart={(e) =>
                                    onDragStart(e, 'aiAction', {
                                        promptTemplateId: tpl.templateId,
                                        prompt: tpl.content,
                                        promptSections:
                                            tpl.contentSections && Object.keys(tpl.contentSections).length > 0
                                                ? normaliseSections(tpl.contentSections)
                                                : normaliseSections({ roleContext: tpl.content || '' }),
                                        databaseType: tpl.databaseType,
                                    })
                                }
                                style={{
                                    '--pal-color': '#10B981',
                                    '--pal-bg': '#ECFDF5',
                                    '--pal-border': '#34D399',
                                } as React.CSSProperties}
                            >
                                <div className="node-palette__item-icon">🤖</div>
                                <div className="node-palette__item-info">
                                    <div className="node-palette__item-label">{tpl.name}</div>
                                    <div className="node-palette__item-desc">Drag to insert as AI Node</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {categories.map((cat) => {
                const nodes = Object.values(NODE_CONFIGS).filter((n) => n.category === cat.key);
                if (nodes.length === 0) return null;
                return (
                    <div key={cat.key} className="node-palette__category">
                        <div className="node-palette__cat-header">
                            <span className="node-palette__cat-title">{cat.title}</span>
                            <span className="node-palette__cat-sub">{cat.subtitle}</span>
                        </div>

                        <div className="node-palette__items">
                            {nodes.map((config) => (
                                <div
                                    key={config.type}
                                    className="node-palette__item"
                                    draggable
                                    onDragStart={(e) => onDragStart(e, config.type)}
                                    style={{
                                        '--pal-color': config.color,
                                        '--pal-bg': config.bgColor,
                                        '--pal-border': config.borderColor,
                                    } as React.CSSProperties}
                                >
                                    <div className="node-palette__item-icon">{config.icon}</div>
                                    <div className="node-palette__item-info">
                                        <div className="node-palette__item-label">{config.label}</div>
                                        <div className="node-palette__item-desc">{config.description}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default NodePalette;
